import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Input, InputNumber, Modal, Select, Table, Tag, message } from "antd";
import { AlertTriangle, ArrowLeft, Plus } from "lucide-react";
import { AN_COLORS, AN_FONT } from "../../../../design-system/tokens";
import { anTableProps, fmtDate, PageHeader, PageShell, RowActionMenu, TableCard, tableEmpty } from "../../../../design-system/tableUi";
import { isAdminUser } from "../../../../utils/helpers";
import {
  createEnrolToken,
  ENROL_MAX_TTL_HOURS,
  ENROL_MAX_USES,
  listEnrolTokens,
  listJumpServer,
  revokeEnrolToken,
} from "../../../../service/sshProxyService";
import { card, CodeBlock, FieldLabel, Note, PanelHead } from "../sshProxyUi";

// Enrolment tokens: onboarding a fleet. One token, bound to a gateway, lets
// many machines onboard themselves — each runs the same command (by hand, or
// through Ansible, cloud-init or SSM) and appears under Endpoints with its own
// setup key and first account scan. The token and command are shown once.
//
// Reached from a gateway's "Enrol machines" action (?gateway=<id>) and from
// the onboarding wizard's next steps.

const apiError = (err, fallback) => {
  const status = err?.response?.status;
  if (status === 403) return "Administrator role required";
  return err?.response?.data?.error || fallback;
};

const tokenState = (t) => {
  if (t.revokedAt) return { label: "Revoked", color: "default" };
  if (new Date(t.expiresAt).getTime() <= Date.now()) return { label: "Expired", color: "default" };
  if (t.maxUses && t.uses >= t.maxUses) return { label: "Used up", color: "default" };
  return { label: "Live", color: "green" };
};

export default function EnrolTokensPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [messageApi, msgCtx] = message.useMessage();
  const [modal, modalCtx] = Modal.useModal();
  const admin = isAdminUser();

  const gatewayFilter = searchParams.get("gateway") ? Number(searchParams.get("gateway")) : undefined;
  const [gateways, setGateways] = useState([]);
  const [tokens, setTokens] = useState(null); // null while loading
  const [listError, setListError] = useState("");

  // Create form
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ jumpServerId: gatewayFilter, name: "", ttlHours: 24, maxUses: 100 });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null); // the create response, shown once

  useEffect(() => {
    listJumpServer({ payload: { pageSize: 200 }, onSuccess: (d) => setGateways(d?.jumpServers || []), onFailure: () => {} });
  }, []);

  const load = useCallback(() => {
    setTokens(null);
    setListError("");
    listEnrolTokens({
      jumpServerId: gatewayFilter,
      onSuccess: setTokens,
      onFailure: (err) => {
        setTokens([]);
        setListError(apiError(err, "Couldn't load enrolment tokens"));
      },
    });
  }, [gatewayFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const gatewayName = useCallback(
    (id) => gateways.find((g) => String(g.id) === String(id))?.serverName || `Gateway #${id}`,
    [gateways],
  );
  const gatewayOptions = useMemo(
    () => gateways.map((g) => ({ value: g.id, label: g.serverName ? `${g.serverName} (${g.publicIpAddress || "—"})` : `Gateway #${g.id}` })),
    [gateways],
  );

  const draftError =
    !draft.jumpServerId
      ? "Choose a gateway"
      : !(draft.ttlHours >= 1 && draft.ttlHours <= ENROL_MAX_TTL_HOURS)
        ? `Expiry must be 1 to ${ENROL_MAX_TTL_HOURS} hours`
        : !(draft.maxUses >= 1 && draft.maxUses <= ENROL_MAX_USES)
          ? `Max uses must be 1 to ${ENROL_MAX_USES}`
          : draft.name.length > 120
            ? "Name is too long"
            : null;

  const create = () => {
    setSaving(true);
    createEnrolToken({
      ...draft,
      onSuccess: (res) => {
        setSaving(false);
        setCreating(false);
        setCreated(res);
        load();
      },
      onFailure: (err) => {
        setSaving(false);
        messageApi.error(apiError(err, "Couldn't create the token"));
      },
    });
  };

  const confirmRevoke = (t) =>
    modal.confirm({
      title: `Revoke "${t.name || "this token"}"?`,
      content: "Machines that haven't enrolled yet can no longer use it. Machines already enrolled are unaffected.",
      okText: "Revoke",
      okButtonProps: { danger: true },
      onOk: () =>
        new Promise((resolve) =>
          revokeEnrolToken({
            id: t.id,
            onSuccess: () => {
              messageApi.success("Token revoked");
              load();
              resolve();
            },
            onFailure: (err) => {
              messageApi.error(
                err?.response?.status === 404 ? "This token is already revoked or expired" : apiError(err, "Couldn't revoke the token"),
              );
              load();
              resolve();
            },
          }),
        ),
    });

  const columns = [
    { title: "Name", key: "name", render: (_, t) => <span style={{ fontWeight: 500, color: AN_COLORS.ink }}>{t.name || "—"}</span> },
    { title: "Gateway", key: "gateway", render: (_, t) => gatewayName(t.jumpServerId) },
    { title: "Uses", key: "uses", width: 110, render: (_, t) => `${t.uses || 0} / ${t.maxUses}` },
    { title: "Expires", key: "expires", width: 180, render: (_, t) => <span style={{ fontSize: 12 }}>{fmtDate(t.expiresAt)}</span> },
    { title: "Created", key: "created", width: 180, render: (_, t) => <span style={{ fontSize: 12 }}>{fmtDate(t.createdAt)}</span> },
    {
      title: "Status",
      key: "state",
      width: 170,
      render: (_, t) => {
        const st = tokenState(t);
        return (
          <span style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
            <Tag color={st.color} style={{ width: "fit-content" }}>{st.label}</Tag>
            {t.revokedAt && <span style={{ fontSize: 11.5, color: AN_COLORS.muted }}>{fmtDate(t.revokedAt)}</span>}
          </span>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 56,
      render: (_, t) =>
        admin && tokenState(t).label === "Live" ? (
          <RowActionMenu items={[{ key: "revoke", label: "Revoke", danger: true, onClick: () => confirmRevoke(t) }]} />
        ) : null,
    },
  ];

  return (
    <PageShell>
      {msgCtx}
      {modalCtx}
      <PageHeader
        eyebrow="PAM · Gateways"
        title="Enrol machines"
        subtitle="Onboard many machines at once. Run the command on each machine, or in Ansible, cloud-init or SSM. Machines appear under Endpoints by themselves."
        actions={
          <span style={{ display: "inline-flex", gap: 8 }}>
            <Button onClick={() => navigate("/pam/jumpServer")} icon={<ArrowLeft size={14} style={{ verticalAlign: -2 }} />}>
              Gateways
            </Button>
            {admin && (
              <Button type="primary" onClick={() => { setDraft((d) => ({ ...d, jumpServerId: d.jumpServerId || gatewayFilter })); setCreating(true); }} icon={<Plus size={14} style={{ verticalAlign: -2 }} />}>
                New token
              </Button>
            )}
          </span>
        }
      />

      <div style={{ display: "grid", gap: 16, fontFamily: AN_FONT }}>
        {created && (
          <div style={{ ...card, padding: 18 }}>
            <PanelHead
              eyebrow="New token"
              title={`Enrolment command${created.tokenInfo?.name ? ` — ${created.tokenInfo.name}` : ""}`}
              desc={`Valid until ${fmtDate(created.tokenInfo?.expiresAt)}, for up to ${created.tokenInfo?.maxUses} machines, through ${gatewayName(created.tokenInfo?.jumpServerId)}.`}
              right={<Button size="small" onClick={() => setCreated(null)}>Close</Button>}
            />
            {created.command ? (
              <div style={{ display: "grid", gap: 10 }}>
                <Note tone="warn" icon={<AlertTriangle size={15} />}>
                  <strong>Shown only now.</strong> Copy it before closing — the token can't be shown again.
                </Note>
                <CodeBlock copyText={created.command} multiline>{created.command}</CodeBlock>
                <div style={{ fontSize: 12.5, color: AN_COLORS.muted }}>
                  Run the command on each machine, or in Ansible, cloud-init or SSM. Machines appear under Endpoints by
                  themselves.
                </div>
              </div>
            ) : (
              <Note tone="warn" icon={<AlertTriangle size={15} />}>
                The token was created, but the command can't be built yet: {created.commandUnavailable || "the gateway hasn't reported its host key yet."}{" "}
                Check the gateway is running, then create a new token.
              </Note>
            )}
          </div>
        )}

        <TableCard>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 14px", flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: AN_COLORS.ink }}>Enrolment tokens</span>
            <Select
              allowClear
              placeholder="All gateways"
              value={gatewayFilter}
              onChange={(v) => setSearchParams(v ? { gateway: String(v) } : {}, { replace: true })}
              options={gatewayOptions}
              style={{ minWidth: 240 }}
            />
          </div>
          {listError && (
            <div style={{ padding: "0 14px 12px" }}>
              <Note tone="danger">{listError}</Note>
            </div>
          )}
          <Table
            {...anTableProps}
            rowKey={(t) => t.id}
            columns={columns}
            dataSource={tokens || []}
            loading={tokens === null}
            pagination={false}
            locale={tableEmpty(tokens === null, "No enrolment tokens yet.")}
            scroll={{ x: true }}
          />
        </TableCard>
      </div>

      <Modal
        open={creating}
        onCancel={() => setCreating(false)}
        footer={null}
        width={640}
        destroyOnClose
        title="New enrolment token"
      >
        <div style={{ display: "grid", gap: 14, fontFamily: AN_FONT }}>
          <label>
            <FieldLabel required>Gateway</FieldLabel>
            <Select
              value={draft.jumpServerId}
              onChange={(v) => setDraft((d) => ({ ...d, jumpServerId: v }))}
              options={gatewayOptions}
              placeholder="The gateway these machines sit behind"
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="label"
            />
          </label>
          <label>
            <FieldLabel hint="optional">Name</FieldLabel>
            <Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="e.g. web fleet, eu-west batch" maxLength={120} />
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <label>
              <FieldLabel required hint={`up to ${ENROL_MAX_TTL_HOURS} (7 days)`}>Expires in (hours)</FieldLabel>
              <InputNumber min={1} max={ENROL_MAX_TTL_HOURS} value={draft.ttlHours} onChange={(v) => setDraft((d) => ({ ...d, ttlHours: v }))} style={{ width: "100%" }} />
            </label>
            <label>
              <FieldLabel required hint={`up to ${ENROL_MAX_USES}`}>Max uses</FieldLabel>
              <InputNumber min={1} max={ENROL_MAX_USES} value={draft.maxUses} onChange={(v) => setDraft((d) => ({ ...d, maxUses: v }))} style={{ width: "100%" }} />
            </label>
          </div>
          {draftError && <div style={{ color: AN_COLORS.danger, fontSize: 12 }}>{draftError}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setCreating(false)}>Cancel</Button>
            <Button type="primary" loading={saving} disabled={!!draftError} onClick={create}>
              Create token
            </Button>
          </div>
        </div>
      </Modal>
    </PageShell>
  );
}
